import { z } from 'zod';
import { IdParams, IsoDateTime, KstDate, Ok, Rate } from '../common.ts';
import { ERROR_KINDS, LANE_ENGINES, OUTCOMES, PAUSE_CATEGORIES, WORKER_AUTH_KINDS } from '../probe-values.ts';
import { defineRoute } from '../route.ts';
import { Egress, WorkerResources } from '../worker.ts';

// ── 워커 관리 (admin) ───────────────────────────────────────────────────
/**
 * ChatGPT 이외 엔진 레인 (261104-gemini-concurrent-lane). 행이 없는 엔진은 목록에 없다 = 꺼짐.
 * 워커의 `enabled` 가 꺼져 있으면 레인이 켜져 있어도 수집하지 않는다
 */
export const WorkerLaneRow = z.object({
  engine: z.enum(LANE_ENGINES),
  enabled: z.boolean(),
  minGapSec: z.number().int(),
  pausedUntil: IsoDateTime.nullable(),
  pauseReason: z.string().nullable(),
  pauseCategory: z.enum(PAUSE_CATEGORIES).nullable(),
  pauseStreak: z.number().int(),
});

export const WorkerRow = z.object({
  id: z.uuid(),
  name: z.string(),
  authKind: z.enum(WORKER_AUTH_KINDS),
  saEmail: z.string().nullable(),
  enabled: z.boolean(),
  minGapSec: z.number().int(),
  pausedUntil: IsoDateTime.nullable(),
  pauseReason: z.string().nullable(),
  alive: z.boolean(),
  lastSeenAt: IsoDateTime.nullable(),
  observedIp: z.string().nullable(),
  network: z.string().nullable(),
  networkLeader: z.boolean(),
  networkPeers: z.number().int(),
  runtime: z
    .object({
      host: z.string(),
      version: z.string(),
      probeVersion: z.string(),
      chromeVersion: z.string().nullable(),
      reportedEgress: Egress.nullable(),
      resources: WorkerResources.nullable(),
    })
    .nullable(),
  currentWork: z.object({ sampleId: z.uuid(), queryText: z.string(), leasedAt: IsoDateTime }).nullable(),
  /**
   * 이 워커를 발급한 등록 토큰 이름. 손으로 만든 워커는 null.
   * `.default` 를 두는 이유 — 트래픽 롤백으로 옛 리비전이 응답해도 이 필드 없이 파싱되게 (auth.ts Me 와 같다)
   */
  enrollTokenName: z.string().nullable().default(null),
  /** 같은 자격증명으로 다른 기계가 붙은 것으로 보인 마지막 시각 — **경고다. 아무것도 막지 않는다** */
  bootConflictAt: IsoDateTime.nullable().default(null),
  /** 정지 사유 범주 — 화면·알림이 한국어 pauseReason 문장을 파싱하지 않게 한다 */
  pauseCategory: z.enum(PAUSE_CATEGORIES).nullable().default(null),
  /**
   * 연속 자동 정지 횟수. ★ 정지 **시간**을 정하지 않는다 — 정지는 늘 30분이다.
   * 만성 차단을 사람이 알아보라고 세는 값이다 (260918-pause-fixed-probe)
   */
  pauseStreak: z.number().int().default(0),
  /** `.default` — 옛 리비전 응답에도 파싱되게 */
  lanes: z.array(WorkerLaneRow).default([]),
  createdAt: IsoDateTime,
});

export const listWorkers = defineRoute({
  method: 'get',
  path: '/api/workers',
  access: 'admin',
  summary: '워커 목록 — 생존은 조회 시 판단한다',
  response: z.object({ workers: z.array(WorkerRow) }),
});

export const createWorker = defineRoute({
  method: 'post',
  path: '/api/workers',
  access: 'admin',
  summary: '워커 발급 — token 방식이면 토큰 원문을 이 응답에서 한 번만 준다',
  body: z
    .object({
      name: z.string().trim().min(1).max(80),
      authKind: z.enum(WORKER_AUTH_KINDS),
      saEmail: z.email().nullable(),
    })
    .strict(),
  response: z.object({ worker: WorkerRow, token: z.string().nullable() }),
});

export const updateWorker = defineRoute({
  method: 'patch',
  path: '/api/workers/{id}',
  access: 'admin',
  summary: '워커 켜기·끄기·간격',
  params: IdParams,
  body: z
    .object({
      name: z.string().trim().min(1).max(80).optional(),
      enabled: z.boolean().optional(),
      minGapSec: z.number().int().min(120).max(86400).optional(),
    })
    .strict(),
  response: z.object({ worker: WorkerRow }),
});

export const pauseWorker = defineRoute({
  method: 'post',
  path: '/api/workers/{id}/pause',
  access: 'admin',
  summary: '수동 정지',
  params: IdParams,
  body: z
    .object({
      hours: z
        .number()
        .min(0.1)
        .max(24 * 30),
      reason: z.string().max(300).nullable(),
    })
    .strict(),
  response: z.object({ worker: WorkerRow }),
});

export const resumeWorker = defineRoute({
  method: 'post',
  path: '/api/workers/{id}/resume',
  access: 'admin',
  summary: '정지 해제 — pause_reason 까지 지운다(프로베이션 없이)',
  params: IdParams,
  response: z.object({ worker: WorkerRow }),
});

const LaneParams = z.object({ id: z.uuid(), engine: z.enum(LANE_ENGINES) });

export const updateWorkerLane = defineRoute({
  method: 'patch',
  path: '/api/workers/{id}/lanes/{engine}',
  access: 'admin',
  summary: '엔진 레인 켜기·끄기·간격 — 행이 없으면 만든다 (261104-gemini-concurrent-lane)',
  params: LaneParams,
  body: z
    .object({
      enabled: z.boolean().optional(),
      minGapSec: z.number().int().min(60).max(86400).optional(),
    })
    .strict(),
  response: z.object({ worker: WorkerRow }),
});

export const resumeWorkerLane = defineRoute({
  method: 'post',
  path: '/api/workers/{id}/lanes/{engine}/resume',
  access: 'admin',
  summary: '레인 정지 해제 — pause_reason 까지 지운다(프로베이션 없이)',
  params: LaneParams,
  response: z.object({ worker: WorkerRow }),
});

export const deleteWorker = defineRoute({
  method: 'delete',
  path: '/api/workers/{id}',
  access: 'admin',
  summary: '워커 폐기(soft delete) — 점유를 즉시 회수한다',
  params: IdParams,
  response: Ok,
});

// ── 등록 토큰 (admin) ───────────────────────────────────────────────────
/**
 * 기계가 스스로 워커를 발급받는 자격증명 (260918-worker-self-enrollment).
 * 한 토큰으로 여러 기계가 등록하고, 기계마다 다른 워커 행 + 워커 토큰이 생긴다.
 */
export const EnrollTokenRow = z.object({
  id: z.uuid(),
  name: z.string(),
  autoEnable: z.boolean(),
  maxWorkers: z.number().int().nullable(),
  /** 이 토큰으로 지금까지 만들어진 워커 수(폐기 제외) */
  workers: z.number().int(),
  expiresAt: IsoDateTime,
  expired: z.boolean(),
  createdAt: IsoDateTime,
});

export const listEnrollTokens = defineRoute({
  method: 'get',
  path: '/api/enroll-tokens',
  access: 'admin',
  summary: '등록 토큰 목록 — 원문은 여기에 없다(발급 응답에서 한 번만)',
  response: z.object({ enrollTokens: z.array(EnrollTokenRow) }),
});

export const createEnrollToken = defineRoute({
  method: 'post',
  path: '/api/enroll-tokens',
  access: 'admin',
  summary: '등록 토큰 발급 — 원문을 이 응답에서 한 번만 준다',
  body: z
    .object({
      name: z.string().trim().min(1).max(80),
      /** 등록된 워커를 바로 켠다. 새 네트워크를 켜는 것은 ToS 미해결 방향이라 기본은 끔 */
      autoEnable: z.boolean(),
      maxWorkers: z.number().int().min(1).max(1000).nullable(),
      expiresInDays: z.number().int().min(1).max(365),
    })
    .strict(),
  /**
   * `serviceUrl` 은 워커가 붙을 **기계 주소**다(run.app). 화면 주소가 아니다 —
   * 커스텀 도메인을 구워 두면 도메인이 옮겨갈 때 그 이미지로 뜬 워커가 전부 끊긴다
   */
  response: z.object({ enrollToken: EnrollTokenRow, token: z.string(), serviceUrl: z.string() }),
});

export const deleteEnrollToken = defineRoute({
  method: 'delete',
  path: '/api/enroll-tokens/{id}',
  access: 'admin',
  summary: '등록 토큰 폐기 — 이미 만들어진 워커는 살려 둔다',
  params: IdParams,
  response: Ok,
});

// ── 상태 화면 (admin) ───────────────────────────────────────────────────
export const health = defineRoute({
  method: 'get',
  path: '/api/health',
  access: 'admin',
  summary: '수집 상태',
  response: z.object({
    now: IsoDateTime,
    today: KstDate,
    scheduler: z.array(
      z.object({
        name: z.string(),
        lastStartedAt: IsoDateTime.nullable(),
        lastOkAt: IsoDateTime.nullable(),
        lastError: z.string().nullable(),
        stale: z.boolean(),
      }),
    ),
    /** 오늘 수집 — 1번 = 쓸 수 있는 샘플(260916-on-demand-collection) */
    collection: z.object({
      /** Σ samples_per_day (활성 질문) */
      target: z.number().int(),
      /** 쓸 수 있는 샘플 — 질문마다 목표까지만 센다 */
      usable: z.number().int(),
      inFlight: z.number().int(),
    }),
    /** 어제 질문마다 목표에 모자란 쓸 수 있는 샘플의 합 — samples_per_day 는 지금 값 기준 */
    missedYesterday: z.number().int(),
    capacity: z.object({
      demandPerDay: z.number().int(),
      /** 이론 공급(86400 ÷ 간격). ★ 밤 시간대 저하를 모른다 — 판단은 achievedPerDay 로 한다 */
      supplyPerDay: z.number().int(),
      utilization: z.number().nullable(),
      /** ★ 옛 예산. 판정에 쓰지 않는다 — 다음 배포에서 제거한다 (260921-capacity-from-measurement) */
      budgetPerDay: z.number().int(),
      /** 실측 창 길이(일). 수요가 0 이던 날은 세지 않는다 */
      windowDays: z.number().int().default(0),
      /** 그 창의 **실제 달성** 일평균 — 용량 판단의 근거다 */
      achievedPerDay: z.number().int().default(0),
      /** 창 전체 결손률. null = 표본 없음 */
      shortfallRatio: z.number().nullable().default(null),
    }),
    workers: z.array(
      z.object({
        id: z.uuid(),
        name: z.string(),
        alive: z.boolean(),
        pausedUntil: IsoDateTime.nullable(),
        pauseReason: z.string().nullable(),
        noAnswer6h: Rate,
        errorKinds24h: z.record(z.string(), z.number().int()),
        minStartGapSec24h: z.number().nullable(),
      }),
    ),
    recentSamples: z.array(
      z.object({
        id: z.uuid(),
        queryText: z.string(),
        workerName: z.string(),
        outcome: z.enum(OUTCOMES).nullable(),
        errorKind: z.enum(ERROR_KINDS).nullable(),
        createdAt: IsoDateTime,
        finishedAt: IsoDateTime.nullable(),
        egressCountry: z.string().nullable(),
      }),
    ),
  }),
});
